import { Component } from "react";

import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import moment from "moment";
import { CookiesProvider } from "react-cookie";
import { Provider as ReduxProvider } from "react-redux";

import createReduxStore from "../../../reducers/store";
import { setLanguage } from "../../api/client.utils";
import DynamicIntlProvider from "../../components/DynamicIntlProvider/DynamicIntlProvider";
import { CfConfigProvider } from "../../hocs/context/withConfig";
import { CfFarmProvider } from "../../hocs/context/withFarm";
import Localization from "../../services/Localization.service";
import LocalStorage from "../../services/LocalStorage.service";
import UiCache from "../../services/UiCache.service";
import { WithToggles } from "../../toggles/WithToggles";

window.resetCache = () => {
  UiCache.deleteParams();
  setTimeout(() => location.reload(), 1000);
};

function saveToCache(state, cacheKey) {
  const { ui } = state;
  if (ui) {
    const uiToCache = {};
    const keysToInclude = ui?.cache;
    if (!keysToInclude) return;

    keysToInclude.forEach((key) => {
      uiToCache[key] = { ...ui[key] };
    });
    UiCache.storeParams(cacheKey, uiToCache);
  }
}

/*
 * For now `farm` prop is required, however it is not being always used by childs
 * (e.g. reset & signup), thus for now, it is mocked. However in future this wrapper
 * should be splitted into wrapper for out-of-the-app views (reset, signup, login) &
 * in-the-app views (the rest...)
 * */
// eslint-disable-next-line import/no-default-export
export default class CfApp extends Component {
  constructor(props) {
    super(props);

    const {
      apiProps,
      cacheKey,
      config,
      farm: { id: farmId } = {
        boundingBox: {},
        code: "",
        countryCode: "",
        id: "",
        name: "",
      },
      reducer,
    } = props;

    UiCache.initialize(farmId.toString());
    this.isCached = Boolean(cacheKey);

    // cache (in)validation
    if (this.isCached && farmId) {
      const cachedState = UiCache.retrieveParams(cacheKey);
      if (cachedState) {
        const UI_CACHE_VERSION =
          LocalStorage.loadFromLocalStorage("UI_CACHE_VERSION");
        const isCacheValid =
          UI_CACHE_VERSION === process.env.BUILD_INFO_APP_COMMIT;
        if (isCacheValid) {
          this.cache = { ui: { ...cachedState } };
        } else {
          UiCache.deleteParams();
          this.cache = null;
        }
      }
      LocalStorage.saveToLocalStorage(
        process.env.BUILD_INFO_APP_COMMIT,
        "UI_CACHE_VERSION",
      );
    }

    const store = reducer
      ? createReduxStore(
          apiProps,
          reducer,
          this.isCached && this.cache ? this.cache : null,
          {
            collapsed: (getState, action, logEntry) => !logEntry.error,
          },
          config.environment,
        )
      : null;

    this.state = {
      store,
    };
  }

  /**
   * Watch out as this doesn't work in the following scenarios:
   * - user refreshes the current browser tab
   * - user closes the current browser tab
   * - user navigates from one app section to another
   *
   *  I actually wonder when this does work in all honesty.
   */
  componentWillUnmount() {
    if (this.isCached) {
      saveToCache(this.state.store?.getState(), this.props.cacheKey);
    }
  }

  render() {
    const { store } = this.state;
    const { apiProps, config, farm, reducer, render, ...rest } = this.props;

    const langId = Localization.getLang();

    setLanguage(langId);
    moment.locale(langId);

    const content = (
      <DynamicIntlProvider locale={langId}>
        <WithToggles>
          <CfConfigProvider config={config}>
            <CfFarmProvider farm={farm}>
              <CookiesProvider defaultSetOptions={{ path: "/" }}>
                {render({
                  config,
                  farm,
                  langId,
                  ...rest,
                })}
              </CookiesProvider>
            </CfFarmProvider>
          </CfConfigProvider>
        </WithToggles>
      </DynamicIntlProvider>
    );

    if (store) {
      return (
        <>
          <ReactQueryDevtools buttonPosition="bottom-left" />
          <ReduxProvider store={store}>{content}</ReduxProvider>
        </>
      );
    }

    return (
      <>
        <ReactQueryDevtools buttonPosition="bottom-left" />
        {content}
      </>
    );
  }
}
